/**
 * URL state for `/reports/contact-imports`.
 * Date params are Organization-timezone calendar days as YYYY-MM-DD.
 */
import {
  CONTACT_IMPORT_REPORT_PAGE_SIZES,
  DEFAULT_CONTACT_IMPORT_REPORT_PAGE_SIZE,
  type ContactImportReportPageSize,
} from "@multi-chat/shared";

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

export type ContactImportReportQuery = {
  search: string;
  from?: string;
  to?: string;
  page: number;
  pageSize: ContactImportReportPageSize;
};

function parseYmd(value: string | null): string | undefined {
  if (!value || !YMD_RE.test(value)) return undefined;
  return value;
}

function parsePositiveInt(value: string | null): number | undefined {
  if (!value) return undefined;
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) return undefined;
  return n;
}

function parsePageSize(value: string | null): ContactImportReportPageSize {
  const n = Number(value);
  if ((CONTACT_IMPORT_REPORT_PAGE_SIZES as readonly number[]).includes(n)) {
    return n as ContactImportReportPageSize;
  }
  return DEFAULT_CONTACT_IMPORT_REPORT_PAGE_SIZE;
}

export function parseContactImportReportQuery(search: string): ContactImportReportQuery {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const from = parseYmd(params.get("from"));
  const to = parseYmd(params.get("to"));
  const query: ContactImportReportQuery = {
    search: (params.get("search") ?? "").trim().slice(0, 200),
    page: Math.max(1, parsePositiveInt(params.get("page")) ?? 1),
    pageSize: parsePageSize(params.get("pageSize")),
  };
  if (from && to) {
    query.from = from <= to ? from : to;
    query.to = from <= to ? to : from;
  }
  return query;
}

export function contactImportReportHref(query: ContactImportReportQuery): string {
  const params = new URLSearchParams();
  const search = query.search.trim().slice(0, 200);
  if (search) params.set("search", search);
  if (query.from && query.to) {
    params.set("from", query.from);
    params.set("to", query.to);
  }
  if (query.page > 1) params.set("page", String(query.page));
  if (query.pageSize !== DEFAULT_CONTACT_IMPORT_REPORT_PAGE_SIZE) {
    params.set("pageSize", String(query.pageSize));
  }
  const qs = params.toString();
  return qs.length > 0 ? `/reports/contact-imports?${qs}` : "/reports/contact-imports";
}

export function hasActiveContactImportFilters(query: ContactImportReportQuery): boolean {
  return Boolean(query.search.trim() || query.from || query.to);
}

export function clearContactImportFilters(query: ContactImportReportQuery): ContactImportReportQuery {
  return {
    search: "",
    page: 1,
    pageSize: query.pageSize,
  };
}
